import {
  MARKET_RESEARCH_MAX_STAGES,
  CLS_MAX_STAGES,
  SOW_VERIFICATION_MAX_STAGES,
  WAGE_DETERMINATION_MAX_STAGES,
  PNM_MAX_STAGES,
  FOPR_MAX_STAGES,
  SERVICES_MAX_STAGES,
  SERVICES_COMBO_MAX_STAGES,
  GPC_MAX_STAGES,
} from './cacheUtils';
import {
  CONSTRUCTION_SOW_GENERATION_STAGE_ORDER,
  ONE_TIME_CONSTRUCTION_PRICE_ESTIMATOR_STAGE_ORDER,
} from '../constants/oneTimeConstructionStages';
import { OTHER_TRANSACTIONS_STAGES } from '../constants/otherTransactionsStages';
import { postAwardStages } from '../constants/workflowStages';
import type { FlowMetadata } from '../types/flow';
import {
  applicableComplexStages,
  COMPLEX_SERVICES_STAGE_CODE,
} from '../api/complexServicesFunctions';
import {
  getMarketResearchStageCount,
  getMarketResearchStagePosition,
  type MarketResearchStageApplicability,
} from '../constants/marketResearchStages';

function marketResearchApplicability(flow: FlowMetadata): MarketResearchStageApplicability {
  const projectedTotal = flow.market_research_stage_total;
  // A projected legacy 12-stage total is an authoritative compatibility
  // envelope even when stale historical flags remain on the record.
  if (projectedTotal === 12) {
    return { brandNameJaRequired: false, dd2579Required: false };
  }
  const completedStages = flow.completedStages ?? [];
  let brandNameJaRequired = flow.market_research_brand_name_required === true;
  let dd2579Required = flow.market_research_dd2579_required === true;
  if (projectedTotal === 14) {
    brandNameJaRequired = true;
    dd2579Required = true;
  } else {
    if (flow.market_research_brand_name_required === undefined) {
      brandNameJaRequired = flow.currentStage === 14 || completedStages.includes(14);
    }
    if (flow.market_research_dd2579_required === undefined) {
      dd2579Required = flow.currentStage === 13 || completedStages.includes(13);
    }
    // The only historical 13-stage Market Research branch was DD/DAF 2579.
    // Preserve its terminal position when old projected metadata has neither
    // of the newer explicit applicability flags.
    if (projectedTotal === 13
      && flow.market_research_brand_name_required === undefined
      && flow.market_research_dd2579_required === undefined
      && !brandNameJaRequired
      && !dd2579Required) {
      dd2579Required = true;
    }
  }
  return {
    brandNameJaRequired,
    dd2579Required,
  };
}

/**
 * Derived display status. Mirrors the old chip logic in FlowSelector,
 * so rows and archive labels stay consistent across the UI.
 */
export function deriveFlowStatus(flow: FlowMetadata): 'completed' | 'in progress' {
  const wf = flow.workflow_type;
  const stages = flow.completedStages || [];
  if (wf === 'other_transactions') return (flow as any).other_transactions_package_ready === true ? 'completed' : 'in progress';
  const rawStatus = (flow.status || 'created').replace('_', ' ');

  // The API owns logical-path completion.  In particular, 206 and 622 are
  // durable stage codes, not the 206th/622nd workflow step.  Ignore malformed
  // or legacy/unknown projections and preserve the established fallbacks.
  if (hasUsableWorkflowProgress(flow)) {
    return flow.workflow_progress!.is_terminal ? 'completed' : 'in progress';
  }

  if (wf === 'clause_logic') {
    if (stages.length >= CLS_MAX_STAGES || stages.includes(103)) return 'completed';
  }
  if (wf === 'wage_determination') {
    if ((flow as any).hasSearchResults) return 'completed';
    if ((flow as any).hasExtraction) return 'in progress';
    return 'in progress';
  }
  if (wf === 'sow_verification') {
    if (stages.length >= SOW_VERIFICATION_MAX_STAGES || stages.includes(201)) return 'completed';
    if ((flow as any).hasEditedDocx) return 'in progress';
  }
  if (wf === 'pnm') {
    if (stages.length >= PNM_MAX_STAGES || stages.includes(402)) return 'completed';
  }
  if (wf === 'fopr') {
    if (flow.standalone_mode) {
      // Standalone FOPR = construction abstract, a single-stage workflow.
      // Any saved stage or an explicit completed status marks it done.
      if (stages.length > 0 || flow.status === 'completed') return 'completed';
    } else if (stages.length >= FOPR_MAX_STAGES || stages.includes(502)) {
      return 'completed';
    }
  }
  if (wf === 'services') {
    if (stages.includes(606)) return 'completed';
  }
  if (wf === 'one_time_construction') {
    return stages.includes(623) ? 'completed' : 'in progress';
  }
  if (wf === 'services_combo') {
    // Canonical completion stage is 701 (Form); Export is just a button.
    if (stages.includes(701)) return 'completed';
  }
  if (wf === 'gpc') {
    return flow.gpc_completion_ready === true ? 'completed' : 'in progress';
  }
  if (wf === 'complex_services') {
    // Terminal completion stage is the always-on `record` (609): a flow is
    // complete iff 609 ∈ completedStages (spec §5.3/§5.6).
    if (stages.includes(COMPLEX_SERVICES_STAGE_CODE.record)) return 'completed';
  }
  if (wf === 'dcma_crr') return stages.includes(911) ? 'completed' : 'in progress';
  if (wf === 'dcma_pbp') {
    return stages.includes(902) ? 'completed' : 'in progress';
  }
  if (wf === 'post_award_actions') {
    // The post-award workspace has exactly three internal workflow stages
    // (1000–1002); it is not a Market Research flow with opaque high codes.
    return stages.includes(1002) ? 'completed' : 'in progress';
  }
  if (wf === 'market_research' || !wf) {
    if (flow.standalone_mode) {
      if (stages.length > 0 || flow.status === 'completed') return 'completed';
    } else {
      const applicability = marketResearchApplicability(flow);
      const hasProjectedPolicy = flow.market_research_stage_total !== undefined
        || applicability.brandNameJaRequired
        || applicability.dd2579Required;
      if (hasProjectedPolicy) {
        const brandReady = !applicability.brandNameJaRequired || (
          stages.includes(14) && flow.market_research_brand_name_ready === true
        );
        const ddReady = !applicability.dd2579Required || (
          stages.includes(13) && flow.market_research_dd2579_ready === true
        );
        return stages.includes(12) && brandReady && ddReady ? 'completed' : 'in progress';
      }
      if (stages.includes(12) || flow.status === 'completed') return 'completed';
    }
  }

  if (rawStatus === 'completed') return 'completed';
  if (rawStatus === 'in progress') return 'in progress';
  return 'in progress';
}

/**
 * Total number of stages for a workflow type — denominator for both Saved
 * Stages and Last Active columns.
 */
export function stageTotalFor(flow: FlowMetadata): number {
  if (hasUsableWorkflowProgress(flow)) return flow.workflow_progress!.total_count;
  const wf = flow.workflow_type;
  if (wf === 'clause_logic') return CLS_MAX_STAGES;
  if (wf === 'sow_verification') return SOW_VERIFICATION_MAX_STAGES;
  if (wf === 'construction_sow_generation') return CONSTRUCTION_SOW_GENERATION_STAGE_ORDER.length;
  if (wf === 'wage_determination') return WAGE_DETERMINATION_MAX_STAGES;
  if (wf === 'pnm') return PNM_MAX_STAGES;
  if (wf === 'fopr') return flow.standalone_mode ? 1 : FOPR_MAX_STAGES;
  if (wf === 'services') return SERVICES_MAX_STAGES;
  if (wf === 'one_time_construction') {
    return flow.standalone_mode
      ? 1
      : ONE_TIME_CONSTRUCTION_PRICE_ESTIMATOR_STAGE_ORDER.length;
  }
  if (wf === 'services_combo') return SERVICES_COMBO_MAX_STAGES;
  if (wf === 'gpc') return GPC_MAX_STAGES;
  if (wf === 'other_transactions') return OTHER_TRANSACTIONS_STAGES.length;
  if (wf === 'post_award_actions') return postAwardStages.length;
  if (wf === 'dcma_crr') return 2;
  if (wf === 'dcma_pbp') return 3;
  if (wf === 'complex_services') {
    // Per-branch enabled-stage count (NOT a constant): the denominator is the
    // number of stages actually applicable for this buy's mode/threshold/pop.
    // setAside=false here (denominator is branch-derived, not set-aside gated).
    return applicableComplexStages(
      flow.acquisition_mode ?? null,
      flow.sat_threshold ?? null,
      flow.pop_structure ?? null,
      false,
    ).length;
  }
  if (flow.standalone_mode) return 1;
  // DD/DAF 2579 is intentionally absent from ordinary Market Research flows.
  // The backend moves a coordination-required flow to currentStage 13 before
  // the form is complete, so that durable marker (or completed stage 13) is
  // the only progress-table signal that the thirteenth stage applies.
  if (wf === 'market_research' || !wf) {
    if (flow.market_research_stage_total !== undefined) return flow.market_research_stage_total;
    const applicability = marketResearchApplicability(flow);
    return getMarketResearchStageCount(applicability);
  }
  return MARKET_RESEARCH_MAX_STAGES;
}

/**
 * Convert a raw stage number to its 1-indexed position within its workflow
 * type. Uses the known base offsets for each workflow's stage range.
 */
function stagePosition(stageNum: number, flow: FlowMetadata): number {
  const wf = flow.workflow_type;
  if (wf === 'market_research' || !wf) {
    return getMarketResearchStagePosition(stageNum, marketResearchApplicability(flow)) ?? 1;
  }
  if (wf === 'one_time_construction') {
    const position = ONE_TIME_CONSTRUCTION_PRICE_ESTIMATOR_STAGE_ORDER.indexOf(
      stageNum as (typeof ONE_TIME_CONSTRUCTION_PRICE_ESTIMATOR_STAGE_ORDER)[number],
    );
    return position >= 0 ? position + 1 : 1;
  }
  if (wf === 'construction_sow_generation') {
    const position = CONSTRUCTION_SOW_GENERATION_STAGE_ORDER.indexOf(
      stageNum as (typeof CONSTRUCTION_SOW_GENERATION_STAGE_ORDER)[number],
    );
    return position >= 0 ? position + 1 : 1;
  }
  if (wf === 'dcma_crr') return stageNum >= 910 ? stageNum - 909 : 1;
  if (wf === 'dcma_pbp') return stageNum >= 900 ? stageNum - 899 : 1;
  if (wf === 'other_transactions') return stageNum >= 1100 ? stageNum - 1099 : 1;
  if (wf === 'post_award_actions') return stageNum >= 1000 ? stageNum - 999 : 1;
  if (stageNum >= 800) return stageNum - 799; // gpc 800..802 → 1..3
  if (stageNum >= 700) return stageNum - 699; // services_combo 700..701 → 1..2
  if (stageNum >= 600) return stageNum - 599; // services 600..606 → 1..7
  if (stageNum >= 500) return stageNum - 499; // fopr 500..502 → 1..3
  if (stageNum >= 400) return stageNum - 400; // pnm 401..402 → 1..2
  if (stageNum >= 300) return stageNum - 300; // wage_det 301..304 → 1..4
  if (stageNum >= 200) return stageNum - 199; // sow 200..201 → 1..2
  if (stageNum >= 100) return stageNum - 100; // cls 101..103 → 1..3
  // market_research / fopr standalone: already 1-indexed
  return stageNum;
}

/**
 * "Stage N/M" label for the last active stage (the highest completed stage
 * the user has reached). Empty string when nothing has been saved yet.
 */
// Minimum "Last Active" stage to show when completedStages is empty.
// Pre-refactor, CLS and Services always showed at least 1/N (since opening
// the flow lands on stage 1); all other workflow types showed 0/N.
function emptyStagesFloor(flow: FlowMetadata): number {
  const wf = flow.workflow_type;
  if (wf === 'clause_logic' || wf === 'services') return 1;
  return 0;
}

function hasUsableWorkflowProgress(flow: FlowMetadata): boolean {
  const progress = flow.workflow_progress;
  return !!(
    progress
    && progress.version >= 1
    && progress.path_id !== 'legacy/unknown'
    && Number.isInteger(progress.completed_count)
    && (progress.active_count === undefined || Number.isInteger(progress.active_count))
    && Number.isInteger(progress.total_count)
    && progress.completed_count >= 0
    && progress.total_count > 0
    && progress.completed_count <= progress.total_count
    && (progress.active_count === undefined || (
      progress.active_count >= progress.completed_count
      && progress.active_count <= progress.total_count
    ))
  );
}

// Wage Determination is now a single page but still has two meaningful
// milestones: extraction (1/2) and search results (2/2). Progress is derived
// from the hasExtraction/hasSearchResults flags (the same source
// deriveFlowStatus uses), since WD does not track these via completedStages.
function wageDetProgress(flow: FlowMetadata): number {
  if ((flow as any).hasSearchResults) return 2;
  if ((flow as any).hasExtraction) return 1;
  return 0;
}

export function deriveLastActiveStageText(flow: FlowMetadata): string {
  if (hasUsableWorkflowProgress(flow)) {
    const { completed_count, active_count, total_count } = flow.workflow_progress!;
    return `${active_count ?? completed_count}/${total_count}`;
  }
  const stages = flow.completedStages || [];
  if (flow.workflow_type === 'one_time_construction' && flow.standalone_mode) {
    return `${stages.includes(623) || flow.status === 'completed' ? 1 : 0}/1`;
  }
  // Standalone flows (Vendor Bids, FOPR Abstract) collapse to a single
  // effective stage — show 1/1 once stage 12 is saved, else 0/1.
  if (flow.standalone_mode) {
    // "touched" heuristic mirrors pre-refactor behavior for flows duplicated from a complete source.
    const touched =
      stages.length > 0 ||
      (flow.updatedAt != null && flow.createdAt != null && flow.updatedAt !== flow.createdAt);
    return `${touched ? 1 : 0}/1`;
  }
  if (flow.workflow_type === 'wage_determination') {
    return `${wageDetProgress(flow)}/${stageTotalFor(flow)}`;
  }
  const total = stageTotalFor(flow);
  if (total <= 0) return '—';
  if (stages.length === 0) return `${emptyStagesFloor(flow)}/${total}`;
  const pos = (flow.workflow_type === 'market_research' || !flow.workflow_type)
    ? Math.max(...stages.map(stage => stagePosition(stage, flow)))
    : stagePosition(Math.max(...stages), flow);
  // Clamp to [0, total] so an off-by-one in stagePosition can never
  // produce nonsensical values like "3/2".
  const clamped = Math.max(0, Math.min(pos, total));
  return `${clamped}/${total}`;
}

export function lastActiveNumeric(flow: FlowMetadata): number {
  if (hasUsableWorkflowProgress(flow)) {
    return flow.workflow_progress!.active_count ?? flow.workflow_progress!.completed_count;
  }
  const stages = flow.completedStages || [];
  if (flow.workflow_type === 'one_time_construction' && flow.standalone_mode) {
    return stages.includes(623) || flow.status === 'completed' ? 1 : 0;
  }
  if (flow.standalone_mode) {
    // "touched" heuristic mirrors pre-refactor behavior for flows duplicated from a complete source.
    const touched =
      stages.length > 0 ||
      (flow.updatedAt != null && flow.createdAt != null && flow.updatedAt !== flow.createdAt);
    return touched ? 1 : 0;
  }
  if (flow.workflow_type === 'wage_determination') {
    return wageDetProgress(flow);
  }
  if (stages.length === 0) return emptyStagesFloor(flow);
  const total = stageTotalFor(flow);
  const pos = (flow.workflow_type === 'market_research' || !flow.workflow_type)
    ? Math.max(...stages.map(stage => stagePosition(stage, flow)))
    : stagePosition(Math.max(...stages), flow);
  return Math.max(0, Math.min(pos, total));
}
